import MapComponent from '@components/map/MapComponent';
import { MarkerClusterer } from '@react-google-maps/api';
import SelectedMarker from 'components/map/SelectedMarker';
import useSelectCityPmc from 'hooks/useSelectCityPmc';
import { default as React, useEffect, useMemo, useState } from 'react';
import { Region } from 'react-native-maps';
import { useAppConfigStore } from 'stores/appConfigStore';
import { clusterStyles } from 'styles/StyleSheetStyles';
import { Maybe, ParkZone } from 'types/generatedSchemaTypes';
import { isAndroid, isIos, isWeb } from 'utils/deviceUtils';
import { MapMarkers } from './MapDataRenderers';
import ParkZonePolygons from './ParkZonePolygons';

type ParkMapProps = {
  showBeforeMapLoaded?: boolean;
  parkingZonesData: Maybe<ParkZone>[] | undefined;
  showBackButton?: boolean;
  showLogo?: boolean;
  mapPadding?: { top: number; left: number; right: number; bottom: number };
  withLTP?: boolean;
  showZoomedTooFar?: boolean;
  skipZoomLevel?: boolean;
  focustOnUserLocation?: boolean;
  initialRegion?: Region;
  extent?: number;
  withCity?: boolean;
  zonesLoading?: boolean;
};

const ParkMap = ({
  showBeforeMapLoaded,
  parkingZonesData,
  showLogo = true,
  mapPadding,
  showBackButton = false,
  withLTP = false,
  showZoomedTooFar = false,
  skipZoomLevel = false,
  focustOnUserLocation = true,
  initialRegion,
  extent,
  withCity = false,
  zonesLoading = false,
}: ParkMapProps) => {
  //https://github.com/JustFly1984/react-google-maps-api/issues/3254#issuecomment-1769133018
  const [isMounted, setIsMounted] = useState(isAndroid ?? isIos);
  const { onItemSelect } = useSelectCityPmc();
  const currentView = useAppConfigStore((state) => state.currentRoute);

  // Memoize web markers with clusterer
  const webMarkers = useMemo(() => {
    return (clusterer: any) =>
      MapMarkers(clusterer, parkingZonesData, onItemSelect, withLTP, withCity);
  }, [parkingZonesData, onItemSelect, withLTP, withCity]);

  // Memoize native markers without clusterer
  const nativeMarkers = useMemo(() => {
    return MapMarkers(
      undefined,
      parkingZonesData,
      onItemSelect,
      withLTP,
      withCity
    );
  }, [parkingZonesData, onItemSelect, withLTP, withCity]);

  useEffect(() => {
    if (isMounted) {
      return;
    }
    setTimeout(() => {
      setIsMounted(true);
    }, 1000);
  }, [isMounted, setIsMounted]);

  return (
    <MapComponent
      mapPadding={mapPadding}
      showLogo={showLogo}
      showBackButton={showBackButton}
      showBeforeMapLoaded={showBeforeMapLoaded}
      showZoomedTooFar={showZoomedTooFar}
      focusOnUserLocation={focustOnUserLocation}
      extent={extent}
      onPress={() => undefined}
      zonesLoading={zonesLoading}
      initialRegion={initialRegion}>
      {isWeb && isMounted && (
        <>
          <SelectedMarker
            parkingZonesData={parkingZonesData}
            withLTP={withLTP}
          />
          <MarkerClusterer
            maxZoom={20}
            styles={clusterStyles}
            enableRetinaIcons
            minimumClusterSize={3}
            imageSizes={[50, 60, 70]}>
            {(clusterer) => <>{webMarkers(clusterer)}</>}
          </MarkerClusterer>
        </>
      )}
      {!isWeb &&
        (currentView?.name === 'park' ||
          currentView?.name === 'cityPmcs' ||
          currentView?.name === 'mapScreen') && //https://github.com/react-native-maps/react-native-maps/issues/5402 Due to current performance problems don't render the map on other screens
        nativeMarkers}
      {!isWeb && (
        <SelectedMarker
          parkingZonesData={parkingZonesData}
          withLTP={withLTP}
          withCity={withCity}
        />
      )}
      <ParkZonePolygons
        skipZoomLevel={skipZoomLevel}
        parkingZones={parkingZonesData}
        withCity={withCity}
        withLTP={withLTP}
      />
    </MapComponent>
  );
};

export default ParkMap;
